import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import cogoToast from "cogo-toast";
import { getComponents, genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import * as SurveyCore from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.css";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

const UpdateSRF = () => {
  const history = useHistory();
  const { project_id } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [components, setComponents] = useState([]);
  const [selectedComponentId, setSelectedComponentId] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonModalTitle, setJsonModalTitle] = useState("");
  const [jsonModalData, setJsonModalData] = useState(null);
  const [srfTemplate, setSrfTemplate] = useState(null);
  const [srfTemplateLoading, setSrfTemplateLoading] = useState(false);
  const [editSrfModalOpen, setEditSrfModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editSurveyModel, setEditSurveyModel] = useState(null);
  const [isSavingSrf, setIsSavingSrf] = useState(false);

  const listProjectComponents = () => {
    if (!project_id) return;
    setIsLoading(true);
    getComponents({ project_id })
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const list = res.data.data || [];
          setComponents(list);
          if (list.length > 0) setSelectedComponentId(list[0].id);
        } else {
          cogoToast.error(res.data?.message || "Failed to load components.", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        cogoToast.error("Failed to load components.", { hideAfter: 5 });
      });
  };

  const loadBeneficiariesWithSrf = () => {
    if (!selectedComponentId) {
      cogoToast.error("Please select a component first.", { hideAfter: 3 });
      return;
    }
    setIsLoading(true);
    genericPost(ENDPOINTS.LIST_BENEFICIARIES_WITH_SRF, {
      component_id: selectedComponentId,
    })
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setRows(res.data.data || []);
        } else {
          cogoToast.error(res.data?.message || "Failed to load SRF list.", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        cogoToast.error("Failed to load SRF list.", { hideAfter: 5 });
      });
  };

  const loadSrfTemplateOnce = () => {
    if (srfTemplate) return;
    setSrfTemplateLoading(true);

    genericPost(ENDPOINTS.TEMPLATE_DETAILS, {
      template_name: "srf-template",
    })
      .then((res) => {
        setSrfTemplateLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data || [];
          const first = Array.isArray(data) ? data[0] : data;
          const templateObj = first?.template || first || null;
          if (templateObj && typeof templateObj === "object") {
            setSrfTemplate(templateObj);
            return;
          }
        }
        cogoToast.error("Failed to load SRF template.", { hideAfter: 5 });
      })
      .catch((e) => {
        setSrfTemplateLoading(false);
        console.error(e);
        cogoToast.error("Failed to load SRF template.", { hideAfter: 5 });
      });
  };

  useEffect(() => {
    listProjectComponents();
    loadSrfTemplateOnce();
  }, [project_id]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const term = search.toLowerCase();
    return rows.filter((r) => {
      const srf = r.srf || {};
      return (
        (r.name || "").toLowerCase().includes(term) ||
        (r.telephone || "").toLowerCase().includes(term) ||
        (r.beneficiary_details?.gender || "").toLowerCase().includes(term) ||
        (r.beneficiary_details?.village_name || "").toLowerCase().includes(term) ||
        (r.beneficiary_details?.camp_name || "").toLowerCase().includes(term) ||
        (r.camp || "").toLowerCase().includes(term) ||
        (r.sync_token || "").toLowerCase().includes(term)
      );
    });
  }, [rows, search]);

  const stats = useMemo(() => {
    const total = rows.length;
    const withSrf = rows.filter((r) => r.srf && typeof r.srf === "object").length;
    const withoutSrf = total - withSrf;
    const progress = total > 0 ? Math.round((withSrf / total) * 100) : 0;
    return { total, withSrf, withoutSrf, progress };
  }, [rows]);

  const openJsonModal = (title, data) => {
    setJsonModalTitle(title);
    setJsonModalData(data || {});
    setJsonModalOpen(true);
  };

  const openEditSrfModal = (row) => {
    if (!srfTemplate) {
      cogoToast.error("SRF template is not loaded yet.", { hideAfter: 3 });
      return;
    }
    const model = new SurveyCore.Model(srfTemplate);
    model.showCompletedPage = false;
    model.completedHtml = "";
    const details = row?.beneficiary_details || {};
    const campName = details.camp_name || "";
    const phone = details.telephone || row?.telephone || "";

    // Pre-fill SRF defaults + beneficiary-derived values on every edit open
    model.data = {
      ...(row.srf || {}),
      reg_purpose: "household_assistance",
      reg_purpose_household_assistance:
        "_1_c_food_assistance__cash_voucher_in_kind_",
      Location: campName,
      reg_region: "SO24",
      reg_district: "SO2401",
      location_id_ssid: "other",
      location_name_other: campName,
      direct_ben_first_name: details.first_name || "",
      direct_ben_middle_name: details.middle_name || "",
      direct_ben_last_name: details.last_name || "",
      direct_ben_age: details.age || "",
      direct_ben_gender: details.gender || "",
      direct_ben_primary_phone_number: phone,
      direct_ben_primary_phone_number_repeat: phone,
      alt_recipient_primary_phone_number: phone,
      alt_recipient_primary_phone_number_repeat: phone,
      household_status: "host_community",
    };
    setEditSurveyModel(model);
    setEditingRow(row);
    setEditSrfModalOpen(true);
  };

  const saveSrfUpdate = () => {
    if (!editingRow || !editSurveyModel) return;

    // Enforce SurveyJS validation before saving
    const isValid = editSurveyModel.validate();
    if (!isValid) {
      cogoToast.error("Please complete all required SRF fields before saving.", {
        hideAfter: 4,
      });
      return;
    }

    const payload = {
      id: editingRow.id,
      srf: editSurveyModel.data || {},
    };

    setIsSavingSrf(true);
    genericPost(ENDPOINTS.UPDATE_BENEFICIARY_SRF, payload)
      .then((res) => {
        setIsSavingSrf(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(res.data.message || "SRF updated successfully.", {
            hideAfter: 4,
          });
          // Update local table row without full reload
          setRows((prev) =>
            prev.map((r) =>
              r.id === editingRow.id
                ? {
                    ...r,
                    srf: payload.srf,
                    updated_at: new Date().toISOString().slice(0, 19).replace("T", " "),
                  }
                : r
            )
          );
          setEditSrfModalOpen(false);
        } else {
          cogoToast.error(res.data?.message || "Failed to update SRF.", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setIsSavingSrf(false);
        console.error(err);
        cogoToast.error("Failed to update SRF.", { hideAfter: 5 });
      });
  };

  const formatLabel = (key) =>
    String(key)
      .replace(/_/g, " ")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const renderPrettyValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      return value.join(", ");
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const renderNestedObjectValue = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return <span>{renderPrettyValue(value)}</span>;
    }

    return (
      <div className="ps-1">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="d-flex justify-content-between border-bottom py-1">
            <span style={{ fontWeight: 600, fontSize: "0.8rem" }}>{formatLabel(k)}</span>
            <span style={{ fontSize: "0.8rem", marginLeft: "10px", textAlign: "right" }}>
              {typeof v === "object" && v !== null && !Array.isArray(v)
                ? renderNestedObjectValue(v)
                : renderPrettyValue(v)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderObjectSections = (obj) => {
    if (!obj || typeof obj !== "object") {
      return <div>-</div>;
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) return <div>-</div>;

    return entries.map(([key, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return (
          <div key={key} className="card mb-2" style={{ border: "1px solid #e9ecef" }}>
            <div
              className="card-header py-1 px-2"
              style={{ fontWeight: 600, fontSize: "0.85rem", background: "#f8f9fa" }}
            >
              {formatLabel(key)}
            </div>
            <div className="card-body p-2">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <tbody>
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <tr key={subKey}>
                        <td style={{ width: "40%", fontWeight: 600, fontSize: "0.82rem" }}>
                          {formatLabel(subKey)}
                        </td>
                        <td style={{ fontSize: "0.82rem" }}>
                          {typeof subValue === "object" && subValue !== null && !Array.isArray(subValue)
                            ? renderNestedObjectValue(subValue)
                            : renderPrettyValue(subValue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div key={key} className="d-flex justify-content-between border-bottom py-1">
          <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{formatLabel(key)}</span>
          <span style={{ fontSize: "0.85rem", marginLeft: "12px", textAlign: "right" }}>
            {renderPrettyValue(value)}
          </span>
        </div>
      );
    });
  };

  return (
    <div>
      <h1>UPDATE SRF</h1>
      <Breadcrumb className="ms-1">
        <BreadcrumbItem>
          <Link to="/ebms">EBMS</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">PROJECTS</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>UPDATE SRF</BreadcrumbItem>
      </Breadcrumb>

      <div className="card mb-3" style={{ padding: "12px" }}>
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label" style={{ fontSize: "0.85rem" }}>
              Component
            </label>
            <Input
              type="select"
              bsSize="sm"
              value={selectedComponentId}
              onChange={(e) => setSelectedComponentId(e.target.value)}
              disabled={isLoading}
            >
              <option value="">Select component</option>
              {components.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Input>
          </div>
          <div className="col-md-2">
            <Button
              color="primary"
              size="sm"
              onClick={loadBeneficiariesWithSrf}
              disabled={isLoading || !selectedComponentId}
            >
              {isLoading ? "Loading..." : "View"}
            </Button>
          </div>
          <div className="col-md-4">
            <label className="form-label" style={{ fontSize: "0.85rem" }}>
              Search
            </label>
            <Input
              type="text"
              bsSize="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, gender, village, camp..."
            />
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <div className="card mb-2" style={{ padding: "10px" }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>SRF Update Progress</div>
            <div style={{ fontSize: "0.85rem" }}>
              Updated: <b>{stats.withSrf}</b> | Remaining: <b>{stats.withoutSrf}</b> | Total: <b>{stats.total}</b>
            </div>
          </div>
          <div className="progress" style={{ height: "10px" }}>
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{ width: `${stats.progress}%` }}
              aria-valuenow={stats.progress}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
          <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
            {stats.progress}% complete
          </div>
        </div>

        <table className="table table-sm table-striped table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: "50px" }}>#</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Gender</th>
              <th>Village Name</th>
              <th>Camp Name</th>
              <th>SRF</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-3">
                  No data found.
                </td>
              </tr>
            )}

            {filteredRows.map((row, idx) => (
              <tr key={row.id || idx} style={{ lineHeight: "1.2" }}>
                <td>{idx + 1}</td>
                <td>{row.name || "-"}</td>
                <td>{row.telephone || "-"}</td>
                <td>{row.beneficiary_details?.gender || "-"}</td>
                <td title={row.beneficiary_details?.village_name || "-"} >
                  {row.beneficiary_details?.village_name
                    ? `${row.beneficiary_details.village_name.substring(0, 15)}${
                        row.beneficiary_details.village_name.length > 15 ? "..." : ""
                      }`
                    : "-"}
                </td>
                <td>{row.beneficiary_details?.camp_name || "-"}</td>
                <td>
                  {row.srf ? (
                    <FaCheckCircle color="#198754" title="SRF available" />
                  ) : (
                    <FaExclamationTriangle color="#ffc107" title="SRF missing" />
                  )}
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => openJsonModal("Beneficiary Details", row.beneficiary_details)}
                    >
                      BNF Details
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => openJsonModal("SRF Object", row.srf)}
                    >
                      SRF
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => openEditSrfModal(row)}
                      disabled={srfTemplateLoading || !srfTemplate}
                      title={srfTemplateLoading ? "Loading SRF template..." : "Edit SRF"}
                    >
                      <i className="fa fa-edit" /> Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={jsonModalOpen} toggle={() => setJsonModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setJsonModalOpen(false)}>{jsonModalTitle}</ModalHeader>
        <ModalBody>
          <div
            style={{
              background: "#fff",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
              padding: "12px",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {renderObjectSections(jsonModalData)}
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={editSrfModalOpen} toggle={() => setEditSrfModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setEditSrfModalOpen(false)}>
          Edit SRF - {editingRow?.name || ""}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {!editSurveyModel ? (
            <div className="text-muted">Preparing form...</div>
          ) : (
            <Survey model={editSurveyModel} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditSrfModalOpen(false)} disabled={isSavingSrf}>
            Cancel
          </Button>
          <Button color="primary" onClick={saveSrfUpdate} disabled={isSavingSrf || !editSurveyModel}>
            {isSavingSrf ? "Saving..." : "Save SRF"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UpdateSRF;

